import { LoadingOverlay } from "@mantine/core";
import { useEffect, useState } from "react";
import { getDaysInMonth } from "../../../../components/custom/date-input/utils/date-utils";
import { LabelStatusCustom } from "../../../../components/custom/label-status-custom";
import { AttendanceRecord } from "../types/attendance";
import Tippy from "@tippyjs/react";
import { IconEye } from "../../../../components/icons/icon-group";
import { ABSENCE_TYPE, ATTENDANCE_MODE, ATTENDANCE_STATUS, CHECK_IN_LOCATION_TYPE } from "../../../../constants/status";
import { convertDateFormat } from "../../../../utils/time-utils";
import { parse, isBefore, format, startOfDay } from "date-fns";
import { vi } from "date-fns/locale";

interface Props {
    dataTable: any[]
    loadingTable: boolean
    dateState: { month: number; year: number }
    // Chế độ chấm công của NV đang xem — lấy từ check-in-status (không phụ thuộc vào có record hay
    // không, vì NV NO_ATTENDANCE KHÔNG BAO GIỜ có attendance_record nên không thể suy ra từ dataTable
    // khi cả tháng rỗng). Dùng làm fallback cho các ngày không có record; ngày CÓ record thì ưu tiên
    // đọc attendance_mode ngay trên record đó (đã được BE gắn sẵn).
    attendanceMode?: ATTENDANCE_MODE;
    onShowDetail: (item: AttendanceRecord[]) => void;
}

const AttendanceManagementTable = ({
    dataTable,
    loadingTable,
    dateState,
    attendanceMode,
    onShowDetail,
}: Props) => {

    const [tableMaxHeight, setTableMaxHeight] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            const headerNav = document.querySelector(".navbar") as HTMLElement;
            const filter = document.getElementById("attendance-personal-filter") as HTMLElement;

            if (headerNav && filter) {
                const headerNavHeight = headerNav.offsetHeight;
                const filterHeight = filter.offsetHeight;
                const remainingHeight = window.innerHeight - headerNavHeight - filterHeight - 104;
                setTableMaxHeight(remainingHeight);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getDayOfWeek = (day: number, month: number, year: number): number => {
        return new Date(year, month - 1, day).getDay();
    };

    const getDayName = (day: number): string => {
        const dayOfWeek = getDayOfWeek(day, Number(dateState.month), Number(dateState.year));
        const days = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        return days[dayOfWeek];
    };

    const getAttendanceByDate = (day: number): AttendanceRecord[] => {
        const dateStr = `${day.toString().padStart(2, '0')}/${String(dateState.month).padStart(2, '0')}/${dateState.year}`;
        return dataTable.filter(record => record.date === dateStr);
    };

    const isToday = (day: number): boolean => {
        const today = new Date();
        return today.getDate() === day &&
            today.getMonth() === Number(dateState.month) - 1 &&
            today.getFullYear() === Number(dateState.year);
    };

    const formatTime = (datetime: string): string => {
        if (!datetime) return '';
        return convertDateFormat(datetime, 'dd/MM/yyyy HH:mm', 'HH:mm')
    };

    const attendanceStatusMap: Record<number, { content: string; backgroundColor: string; textColor: string }> = {
        [ATTENDANCE_STATUS.NONE]: { content: "Không có", backgroundColor: "bg-gray-100", textColor: "text-gray-700" },
        [ATTENDANCE_STATUS.ON_TIME]: { content: "Đúng giờ", backgroundColor: "bg-green-100", textColor: "text-green-700" },
        [ATTENDANCE_STATUS.LATE]: { content: "Muộn", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
        [ATTENDANCE_STATUS.LEAVE_EARLY]: { content: "Về sớm", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
        [ATTENDANCE_STATUS.NOT_CHECKED_IN]: { content: "Chưa check in", backgroundColor: "bg-red-100", textColor: "text-red-700" },
        [ATTENDANCE_STATUS.NOT_CHECKED_OUT]: { content: "Chưa check out", backgroundColor: "bg-red-100", textColor: "text-red-700" },
    };

    const checkInLocationMap: Record<number, { content: string; backgroundColor: string; textColor: string }> = {
        [CHECK_IN_LOCATION_TYPE.OUTSIDE_COMPANY]: { content: "Ngoài công ty", backgroundColor: "bg-orange-100", textColor: "text-orange-700" },
        [CHECK_IN_LOCATION_TYPE.INSIDE_COMPANY]: { content: "Trong công ty", backgroundColor: "bg-blue-100", textColor: "text-blue-700" },
    };

    const getStatusBadge = (attendance: AttendanceRecord | null, isCheckIn: boolean) => {
        if (!attendance) return null;
        // NO_ATTENDANCE (quản lý/tài xế/kiểm đếm) — không bao giờ check-in được (chặn ở BE), hiện
        // "Chưa check in/out" đỏ như thiếu sót là hiểu nhầm. Hiện trung tính thay thế.
        if (attendance.attendance_mode === ATTENDANCE_MODE.NO_ATTENDANCE) {
            return <LabelStatusCustom content="Không chấm công" backgroundColor="bg-gray-100" textColor="text-gray-500" className="min-w-28" />;
        }
        if (attendance.attendance_mode === ATTENDANCE_MODE.PART_TIME) {
            if (isCheckIn) {
                if (attendance.check_in_at) {
                    return <LabelStatusCustom content="Đúng giờ" backgroundColor="bg-green-100" textColor="text-green-700" className="min-w-28" />;
                }
                return <LabelStatusCustom content="Chưa check in" backgroundColor="bg-gray-100" textColor="text-gray-500" className="min-w-28" />;
            } else {
                if (attendance.check_out_at) {
                    return <LabelStatusCustom content="Đúng giờ" backgroundColor="bg-green-100" textColor="text-green-700" className="min-w-28" />;
                }
                return <LabelStatusCustom content="Chưa check out" backgroundColor="bg-yellow-100" textColor="text-yellow-700" className="min-w-28" />;
            }
        }
        let badge
        if (isCheckIn) {
            badge = attendanceStatusMap[attendance.check_in_status] ?? attendanceStatusMap[ATTENDANCE_STATUS.NONE];
        } else {
            badge = attendanceStatusMap[attendance.check_out_status] ?? attendanceStatusMap[ATTENDANCE_STATUS.NONE];
        }
        return <LabelStatusCustom
            content={badge.content}
            textColor={badge.textColor}
            backgroundColor={badge.backgroundColor}
            className="min-w-28"
        />
    };

    const getCheckInLocationBadge = (attendance: AttendanceRecord | null, isCheckIn: boolean) => {
        if (!attendance) return null;
        let badge
        if (isCheckIn) {
            badge = checkInLocationMap[attendance.check_in_scope];
        } else {
            badge = checkInLocationMap[attendance.check_out_scope];
        }
        return <LabelStatusCustom
            content={badge?.content ?? "Không xác định"}
            backgroundColor={badge?.backgroundColor ?? "bg-gray-100"}
            textColor={badge?.textColor ?? "text-gray-700"}
            className="min-w-28"
        />
    };

    /**
     * "Ca làm việc" cho 1 ngày — mirror đúng logic đã dùng ở `attendance-management-table.tsx` (trang
     * admin), áp dụng cho cả 3 mode thay vì chỉ SHIFT như trước: NO_ATTENDANCE không có khái niệm ca;
     * FIXED hiện lịch cố định (kể cả ngày nghỉ tuần theo lịch, phân biệt với "chưa cấu hình gì" = "-");
     * SHIFT giữ nguyên khối xanh cũ. Nhận cả `attendanceMode` (fallback khi ngày không có record — vd
     * ngày nghỉ hợp lệ) lẫn record thật (ưu tiên đọc `attendance_mode` ngay trên nó nếu có).
     */
    const renderShiftCell = (records: AttendanceRecord[], fallbackMode?: ATTENDANCE_MODE) => {
        const mode = records[0]?.attendance_mode ?? fallbackMode;

        if (mode === ATTENDANCE_MODE.NO_ATTENDANCE) {
            return <span className="text-gray-400">Không chấm công</span>;
        }

        if (mode === ATTENDANCE_MODE.PART_TIME) {
            return (
                <div className='flex items-stretch justify-center w-full p-2 text-left text-purple-600 border border-purple-500 rounded-md bg-purple-500/10'>
                    <span className="flex-shrink-0 w-1 bg-purple-500 rounded-full" />
                    <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                        <span className="text-base font-bold">05:00 - 22:00</span>
                        <span className="text-sm">Partime</span>
                    </div>
                </div>
            );
        }

        const shiftRecord = records.find(r => r.shift?.id && r.shift.id !== '0' && r.absence_type !== ABSENCE_TYPE.NO_SHIFT);
        if (shiftRecord) {
            return (
                <div className='flex items-stretch justify-center w-full p-2 text-left text-blue-500 border border-blue-500 rounded-md bg-blue-500/10'>
                    <span className="flex-shrink-0 w-1 bg-blue-500 rounded-full" />
                    <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                        <span className="text-base font-bold">{shiftRecord.shift.start_time} - {shiftRecord.shift.end_time}</span>
                        <span className="text-xs">{shiftRecord.shift.name}</span>
                    </div>
                </div>
            );
        }

        const workSchedule = records.find(r => r.work_schedule)?.work_schedule;
        if (workSchedule) {
            if (!workSchedule.is_working) {
                return (
                    <div className="flex items-stretch justify-center w-full p-2 text-left text-gray-500 border border-gray-300 rounded-md bg-gray-100">
                        <span className="flex-shrink-0 w-1 bg-gray-400 rounded-full" />
                        <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                            <span className="text-sm font-semibold">Nghỉ theo lịch</span>
                            <span className="text-sm">{workSchedule.name}</span>
                        </div>
                    </div>
                );
            }
            return (
                <div className='flex items-stretch justify-center w-full p-2 text-left text-teal-600 border border-teal-500 rounded-md bg-teal-500/10'>
                    <span className="flex-shrink-0 w-1 bg-teal-500 rounded-full" />
                    <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                        <span className="text-base font-bold">{workSchedule.start_time} - {workSchedule.end_time}</span>
                        <span className="text-sm">{workSchedule.name}</span>
                    </div>
                </div>
            );
        }

        return <span className="text-gray-400">-</span>;
    };

    const renderCheckLabel = (dateTime: string, timeString: string, label: string) => {
        const now = new Date();

        const date = dateTime
            ? parse(dateTime, "dd/MM/yyyy", new Date(), { locale: vi })
            : null;

        const time = timeString
            ? parse(timeString, "dd/MM/yyyy HH:mm", new Date(), { locale: vi })
            : null;

        let content = "";
        let backgroundColor = "";
        let textColor = "";

        if (time && !isNaN(time.getTime())) {
            // Có giờ check in/out
            content = `${format(time, "HH:mm")}`;
            backgroundColor = "bg-green-100";
            textColor = "text-green-700";
        } else if (date && isBefore(startOfDay(date), startOfDay(now))) {
            // Ngày đã qua nhưng không có giờ check
            content = `Quên ${label.toLowerCase()}`;
            backgroundColor = "bg-red-100";
            textColor = "text-red-700";
        } else {
            // Ngày hôm nay hoặc tương lai, chưa check
            content = `Chưa ${label.toLowerCase()}`;
            backgroundColor = "bg-orange-100";
            textColor = "text-orange-700";
        }

        return (
            <LabelStatusCustom
                content={content}
                backgroundColor={backgroundColor}
                textColor={textColor}
                className="min-w-32"
            />
        );
    };

    const daysInMonth = getDaysInMonth(dateState.month - 1, dateState.year);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
        <div className="relative overflow-auto datatables min-h-52" style={{ height: tableMaxHeight }}>
            <LoadingOverlay visible={loadingTable} zIndex={1000} overlayProps={{ radius: "sm", blur: 1 }} />
            <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                    <tr>
                        <th className="p-4 font-semibold text-left text-gray-700 border-b-2 border-gray-200 min-w-40">Ngày chấm công</th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-52">
                            <div className="flex flex-col">
                                <span>Thời gian</span>
                                <span className="text-xs font-normal">(Check in - Check out)</span>
                            </div>
                        </th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-56">Ca làm việc</th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-48"><span>Trạng thái<br />check in</span></th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-48"><span>Trạng thái<br />check out</span></th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-28">Đi muộn</th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-28">Về sớm</th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-40"><span>Vị trí<br />check in</span></th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-40"><span>Vị trí<br />check out</span></th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200"><span>Ghi chú<br />duyệt phiếu</span></th>
                        <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-32">Hành động</th>
                    </tr>
                </thead>
                <tbody>
                    {days.map(day => {
                        const records = getAttendanceByDate(day);
                        const today = isToday(day);

                        return (
                            <tr
                                key={day}
                                className={`border-b transition-colors 
                                    ${today
                                        ? "bg-blue-50 border-blue-200 hover:bg-blue-50"
                                        : "border-gray-100 hover:bg-gray-50"
                                    }`}
                            >
                                {/* Ngày chấm công */}
                                <td className="p-4">
                                    <div className="flex flex-col">
                                        <span className={`font-semibold ${today ? 'text-blue-700' : 'text-gray-700'}`}>
                                            {String(day).padStart(2, "0")}/{String(dateState.month).padStart(2, "0")}/{dateState.year}
                                        </span>
                                        <span className={`text-sm ${today ? 'text-blue-600' : 'text-gray-500'}`}>
                                            {getDayName(day)}
                                        </span>
                                        {today && (
                                            <LabelStatusCustom
                                                content='Hôm nay'
                                                textColor="text-blue-700"
                                                backgroundColor="bg-blue-100"
                                                className="w-fit"
                                            />
                                        )}
                                    </div>
                                </td>
                                {/* Thời gian: Check in - Check out */}
                                <td className="p-4 text-center">
                                    {records.length > 0 ? (
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                <div key={r.id} className="flex items-center justify-center gap-1">
                                                    {renderCheckLabel(r.date, r.check_in_at, 'Check in')}
                                                    <span className="text-gray-500">-</span>
                                                    {renderCheckLabel(r.date, r.check_out_at, 'Check out')}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>

                                {/* Ca làm việc */}
                                <td className="p-4 text-center">
                                    {renderShiftCell(records, attendanceMode)}
                                </td>

                                {/* Trạng thái check in */}
                                <td className="p-4 text-center">
                                    <div className="flex flex-col gap-1">
                                        {records.length > 0 ? records.map(r => (
                                            <div key={r.id}>{getStatusBadge(r, true)}</div>
                                        )) : attendanceMode === ATTENDANCE_MODE.NO_ATTENDANCE ? (
                                            <LabelStatusCustom content="Không chấm công" backgroundColor="bg-gray-100" textColor="text-gray-500" className="min-w-28" />
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </div>
                                </td>
                                {/* Trạng thái check out */}
                                <td className="p-4 text-center">
                                    <div className="flex flex-col gap-1">
                                        {records.length > 0 ? records.map(r => (
                                            <div key={r.id}>{getStatusBadge(r, false)}</div>
                                        )) : attendanceMode === ATTENDANCE_MODE.NO_ATTENDANCE ? (
                                            <LabelStatusCustom content="Không chấm công" backgroundColor="bg-gray-100" textColor="text-gray-500" className="min-w-28" />
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </div>
                                </td>
                                {/* Đi muộn */}
                                <td className="p-4 text-center">
                                    <div className="flex flex-col gap-1">
                                        {records.map(r => (
                                            r.minutes_late > 0 ? (
                                                <LabelStatusCustom
                                                    key={r.id}
                                                    content={`${r.minutes_late} phút`}
                                                    textColor="text-orange-700"
                                                    backgroundColor="bg-orange-100"
                                                    className="min-w-20"
                                                />
                                            ) : (
                                                <span key={r.id} className="text-gray-400">-</span>
                                            )
                                        ))}
                                    </div>
                                </td>
                                {/* Về sớm */}
                                <td className="p-4 text-center">
                                    <div className="flex flex-col gap-1">
                                        {records.map(r => (
                                            r.minutes_early > 0 ? (
                                                <LabelStatusCustom
                                                    key={r.id}
                                                    content={`${r.minutes_early} phút`}
                                                    textColor="text-purple-700"
                                                    backgroundColor="bg-purple-100"
                                                    className="min-w-20"
                                                />
                                            ) : (
                                                <span key={r.id} className="text-gray-400">-</span>
                                            )
                                        ))}
                                    </div>
                                </td>
                                {/* Vị trí check in */}
                                <td className="p-4 text-center">
                                    <div className="flex flex-col gap-1">
                                        {records.map(r => (
                                            <div key={r.id}>{getCheckInLocationBadge(r, true)}</div>
                                        ))}
                                    </div>
                                </td>
                                {/* Vị trí check out */}
                                <td className="p-4 text-center">
                                    <div className="flex flex-col gap-1">
                                        {records.map(r => (
                                            <div key={r.id}>{getCheckInLocationBadge(r, false)}</div>
                                        ))}
                                    </div>
                                </td>
                                {/* Ghi chú duyệt phiếu */}
                                <td className="p-4 text-center">
                                    <div className="m-auto min-w-40 max-w-96">
                                        {records.length > 0 ? (
                                            <span className="break-words whitespace-normal">{records[records.length - 1]?.note || "-"}</span>
                                        ) : (<span className="text-gray-400">-</span>)}
                                    </div>
                                </td>
                                {/* Hành động */}
                                <td className="p-4 text-center">
                                    <div className="flex justify-center gap-2">
                                        {records.length > 0 ? (
                                            <>
                                                <Tippy content="Chi tiết">
                                                    <button type="button" onClick={() => onShowDetail(records)}>
                                                        <IconEye />
                                                    </button>
                                                </Tippy>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default AttendanceManagementTable